import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { LocaleLink } from "@/components/LocaleLink";
import { useT } from "@/lib/i18n";
import { SIZE_BUCKETS, SIZE_SEARCH_QUERY, bucketize } from "@/lib/sizes";
import { fetchAllProducts } from "@/lib/shopify";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/en/storrelse/")({
  loader: async () => {
    // Fetch a wide net of lamp products with diameter mentions, then count
    // how many fall into each bucket. Cached for 5 min on the edge.
    const products = await fetchAllProducts({
      query: SIZE_SEARCH_QUERY,
      sortKey: "BEST_SELLING",
      reverse: false,
      max: 1000,
    });

    const buckets = bucketize(products);
    const counts: Record<string, number> = {};
    for (const [slug, list] of buckets) counts[slug] = list.length;
    return { counts };
  },
  head: () => ({
    meta: [
      { title: "Shop by size — Farveladeland" },
      {
        name: "description",
        content:
          "Find vintage Murano lamps and pendants by diameter — from small 15 cm table lamps to large 40+ cm chandeliers.",
      },
      { property: "og:title", content: "Shop by size — Farveladeland" },
      {
        property: "og:description",
        content:
          "Six sizes, one land. Browse vintage lamps by diameter (Ø) in centimetres.",
      },
      { rel: "canonical", href: `${SITE_URL}/en/storrelse` } as never,
    ],
  }),
  component: SizesIndex,
});

function SizesIndex() {
  const { counts } = Route.useLoaderData();
  const { t, locale } = useT();

  return (
    <>
      <section className="pt-40 pb-12 md:pt-52 md:pb-16">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <p className="eyebrow">{t("sizes.eyebrow")}</p>
          <h1 className="mt-4 font-display text-[clamp(3rem,7vw,6rem)] leading-[0.95]">
            {t("sizes.titleA")}
            <br />
            <em className="text-terracotta">{t("sizes.titleB")}</em>
          </h1>
          <p className="mt-6 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground">
            {t("sizes.body")}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-32 md:px-10">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-3">
          {SIZE_BUCKETS.filter((b) => (counts[b.slug] ?? 0) > 0).map((b, i) => {
            const count = counts[b.slug] ?? 0;
            const label = locale === "da" ? b.labelDa : b.labelEn;
            const intro = locale === "da" ? b.introDa : b.introEn;
            // Visualise size with a circle whose diameter scales with the
            // bucket midpoint — capped so 40+ doesn't overflow the card.
            const mid = b.max === Infinity ? 50 : (b.min + b.max) / 2;
            const visual = Math.min(180, 40 + mid * 2.4); // px
            return (
              <motion.div
                key={b.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.05,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <LocaleLink
                  to="/storrelse/$range"
                  params={{ range: b.slug }}
                  className={`group relative flex aspect-[4/5] flex-col justify-between overflow-hidden rounded-sm p-6 transition-transform duration-500 ease-out hover:-translate-y-1 ${b.panel}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-foreground/55">
                      {b.italian}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-foreground/45">
                      {count > 0 ? t("size.count", { n: count }) : ""}
                    </span>
                  </div>

                  {/* Scaled-diameter circle as visual cue */}
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-30 transition-opacity duration-500 group-hover:opacity-50">
                    <span
                      className="block rounded-full border border-foreground/30"
                      style={{ width: visual, height: visual }}
                      aria-hidden
                    />
                  </div>

                  <div className="relative">
                    <h2 className="font-display text-3xl leading-tight text-foreground md:text-4xl">
                      {label}
                    </h2>
                    <p className="mt-2 line-clamp-2 max-w-[28ch] text-sm leading-snug text-foreground/70">
                      {intro}
                    </p>
                  </div>
                </LocaleLink>
              </motion.div>
            );
          })}
        </div>
      </section>
    </>
  );
}
